import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import DashboardRequestItem from "@/components/pages/dashboard/components/DashboardRequestItem";
import { useDashboardGuestRequestsInfinite } from "@/components/pages/dashboard/hooks/useDashboardGuestRequestsInfinite";

const D = "requests.dashboard" as const;

export const DashboardRequestsContainer = () => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const {
    requests,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useDashboardGuestRequestsInfinite();

  useEffect(() => {
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { root, rootMargin: "0px 0px 120px 0px", threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (isLoading && requests.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-2 py-6">
        <p className="text-sm text-neutral-400">{t(`${D}.loadingPreviews`)}</p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1"
    >
      {requests.length === 0 ? (
        <p className="px-1 py-2 text-center text-xs text-neutral-500">
          {t(`${D}.noPreviewsToday`)}
        </p>
      ) : null}
      {requests.map((request) => (
        <DashboardRequestItem key={request.id} request={request} />
      ))}
      {hasNextPage ? (
        <div ref={sentinelRef} className="h-1 w-full shrink-0" aria-hidden />
      ) : null}
      {isFetchingNextPage ? (
        <p className="px-1 pb-2 text-center text-xs text-neutral-400">
          {t(`${D}.loadingMore`)}
        </p>
      ) : null}
    </div>
  );
};
